import { useApiClient } from "@/lib/api/api.client";
import { useAuth } from "@frontegg/react";
import { useQueryClient } from "@tanstack/react-query";
import { useInvalidateConversations } from "../conversations.api";
import { useInvalidateThreads } from "../threads.api";
import type { Thread } from "../threads.utils";

/**
 * Rename thread: mutation + invalidation on success. Pending state is surfaced
 * via isPending so callers can show loaders.
 */
export function useRenameThread({
  thread,
  onError,
  onSuccess,
  assistantId,
}: {
  thread: Thread;
  onError?: () => void;
  onSuccess?: () => void | Promise<void>;
  assistantId: string;
}) {
  const { user } = useAuth();

  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const invalidateThreads = useInvalidateThreads();
  const invalidateConversations = useInvalidateConversations();

  const mutation = apiClient.useMutation(
    "patch",
    "/threads/{assistant_id}/details/{thread_id}",
    {
      onError,
      onSuccess: async () => {
        // Invalidate threads list
        if (user?.id) {
          await invalidateThreads({
            assistantId,
            userId: user.id,
          });
        }

        // The chat thread list is driven by the conversations query.
        if (assistantId) await invalidateConversations({ assistantId });

        // Invalidate single thread
        if (thread.id) {
          const threadQueryOptions = apiClient.queryOptions(
            "get",
            "/threads/{assistant_id}/details/{thread_id}",
            {
              params: {
                path: {
                  assistant_id: assistantId,
                  thread_id: thread.id,
                },
              },
            },
          );
          await queryClient.invalidateQueries(threadQueryOptions);
        }

        // Call onSuccess callback
        await onSuccess?.();
      },
    },
  );

  function rename(newTitle: string, previousTitle: string) {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === previousTitle) return;
    if (!thread.id || !assistantId) return;

    mutation.mutate({
      params: {
        path: {
          assistant_id: assistantId,
          thread_id: thread.id,
        },
      },
      body: { title: trimmed },
    });
  }

  return {
    rename,
    isPending: mutation.isPending,
  };
}
